import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import Icon from "components/Icon";
import {invalidate, prefetchNearbyPages} from "services/query-client";
import {InformationCircleIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import ConfirmModal from "components/ConfirmModal";
import Dropdown from "components/Dropdown";
import {FactTemplate} from "types/FactTemplate";
import {toTitleCase} from "services/string";
import {useOperator} from "context/OperatorContext";

function FactTemplateList() {
  const [page, setPage] = useState<number>(1)

  const facts = useQuery(
    ['fact-templates.all', {page}],
    () => ngage.factTemplates.all(page),
    {keepPreviousData: true}
  )

  const [deletingFactTemplate, setDeletingFactTemplate] = useState<FactTemplate | null>(null);
  const {mutateAsync: deleteFactTemplate} = useMutation(ngage.factTemplates.destroy)

  const {termFor} = useOperator()
  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prepare action handlers
  async function handleDeleteFactTemplate(factTemplate: FactTemplate) {
    if (factTemplate.id) {
      try {
        await deleteFactTemplate(factTemplate.id)

        setDeletingFactTemplate(null)
        notify("success", `${termFor('Fact')} Deleted`, `${factTemplate.name} has been permanently removed`)

        return invalidate('fact-templates.all')
      } catch (error) {
        setDeletingFactTemplate(null)
        notify("error", `${termFor('Fact')} Not Deleted`, `${factTemplate.name} could not be removed`)
      }
    }
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('fact-templates.all', page, ngage.factTemplates.all)

  // Prepare page actions
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/fact-templates/new')}>New</Button>

  return (
    <Page title={termFor('Facts')}
          actions={actions}
          breadcrumbs={{'App Content': '/', 'Site': '/', [termFor('Facts')]: '/fact-templates'}}
    >
      <Table paginationData={facts.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>Uses</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={facts.isLoading}>

          {facts?.data?.data && facts.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <InformationCircleIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('facts')}</span>
                </div>
              </TableData>
            </TableRow>
            : facts?.data?.data?.map((factTemplate, index) => (
              <TableRow key={factTemplate.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position="left">
                        {[
                          // {name: 'View', icon: EyeIcon, onClick: () => navigate(`/fact-templates/${factTemplate.id}`)},
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/fact-templates/${factTemplate.id}/edit`)},
                          {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingFactTemplate(factTemplate), disabled: factTemplate.use_count > 0}
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <Icon name={factTemplate.icon} className="h-5 w-5 mr-2 text-gray-500"/> {factTemplate.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{toTitleCase(factTemplate.type)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{factTemplate.use_count.toLocaleString()}</TableData>
                <TableData>{formattedDateTime(factTemplate.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      // {name: 'View', icon: EyeIcon, onClick: () => navigate(`/fact-templates/${factTemplate.id}`)},
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/fact-templates/${factTemplate.id}/edit`)},
                      {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingFactTemplate(factTemplate), disabled: factTemplate.use_count > 0}
                    ]}
                  </Dropdown>
                </TableData>

                {/* Delete modal */}
                <ConfirmModal
                  show={deletingFactTemplate === factTemplate}
                  type={ConfirmModal.types.DANGER}
                  title={`Delete ${termFor('Fact')}`}
                  body={`Are you sure you want to permanently delete '${factTemplate.name}'? This action cannot be undone.`}
                  approveLabel="Delete"
                  onApprove={() => handleDeleteFactTemplate(factTemplate)}
                  onCancel={() => setDeletingFactTemplate(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default FactTemplateList;